import type { AcquisitionType, Lead } from "./types.js";

type ReviewDecisionLead = Pick<Lead, "acquisitionPriority" | "reviewNotes">;
type OutreachEligibilityLead = Pick<Lead, "acquisitionType" | "reviewStatus">;
type ReviewStateLead = Pick<Lead, "acquisitionType" | "reviewStatus">;
type OutreachEmailLead = Pick<Lead, "email" | "emailQuality" | "hasUsableEmail">;
type CrawlStateLead = Pick<Lead, "crawlStatus">;

const blockedEmailDomains = new Set(["example.com", "example.org", "example.net", "sentry-next.wixpress.com"]);
const blockedEmailLocalParts = new Set(["your", "ihre", "example", "email", "test", "name", "user"]);

function isAcquisitionCandidateType(value?: AcquisitionType | null) {
  return value === "automation" || value === "distress" || value === "transformation";
}

export function isReviewedAcquisitionCandidate(lead: OutreachEligibilityLead) {
  const reviewed = lead.reviewStatus === "reviewed" || lead.reviewStatus === "qualified";
  return reviewed && isAcquisitionCandidateType(lead.acquisitionType);
}

export function hasLeadReviewDecision(lead: ReviewStateLead) {
  const decidedStatus = !["new", "audit"].includes(lead.reviewStatus);
  const decidedAcquisition = Boolean(lead.acquisitionType && lead.acquisitionType !== "unassigned");
  return decidedStatus || decidedAcquisition;
}

export function hasUsableLeadEmail(lead: OutreachEmailLead) {
  if (lead.hasUsableEmail !== undefined) return lead.hasUsableEmail;
  if (lead.emailQuality === "direct" || lead.emailQuality === "generic") return true;
  const email = lead.email?.trim().toLowerCase();
  if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return false;
  const [localPart, domain] = email.split("@");
  if (!localPart || !domain || blockedEmailDomains.has(domain) || blockedEmailLocalParts.has(localPart)) return false;
  return !/^(?:no-?reply|do-?not-?reply|noreply|donotreply)$/i.test(localPart);
}

export function hasReliableCrawlResult(lead: CrawlStateLead) {
  return lead.crawlStatus === "success" || lead.crawlStatus === "partial" || lead.crawlStatus === "no_website";
}

export function reviewDecisionPayload(lead: ReviewDecisionLead, acquisitionType: AcquisitionType, acquisitionPriority?: number | null) {
  const candidate = isAcquisitionCandidateType(acquisitionType);
  return {
    reviewStage: acquisitionType === "blocked" ? "blocked" : "reviewed",
    acquisitionType,
    acquisitionPriority: candidate ? acquisitionPriority ?? lead.acquisitionPriority ?? 2 : null,
    reviewNotes: lead.reviewNotes ?? ""
  };
}
